<style lang="scss">
  @import '~foundation-sites/scss/util/util';

  .custom-input {
    position: relative;
    text-align: center;

    &.custom-input-hide {
      &::before { content: attr(data-formatted); }
      & input {
        color: #FFFFFF;
      }
    }

    input {
      margin: 0 auto;
      border: 1px solid #ECECEC;
      transition: 125ms background;
      text-align: center;

      &[disabled] {
        color: #ECECEC !important;
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        user-select: none;
        pointer-events: none;
      }
      &::-moz-focus-inner {
          border: 0;
          padding: 0;
      }

      &[type=number] {
        -moz-appearance: textfield;

        &::-webkit-inner-spin-button, &::-webkit-outer-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
      }
    }

    &.disabled::before {
      cursor: not-allowed;
    }

    &::before {
      content: '';
      position: absolute;
      padding: 0.5rem;
      top: -1px;
      left: 0;
      bottom: 0;
      right: 0;
      white-space: nowrap;
      -webkit-touch-callout: none;
      -webkit-user-select: none;
      user-select: none;
      pointer-events: none;
    }
  }
</style>

<template>
  <div class="custom-input" :class="{ 'custom-input-hide': showOverlay }" :data-formatted="filtered(propertyValue)">
    <input
      :id="id"
      :type="type"
      class="vs-input"
      :value="propertyValue"
      :min="propertyRange.min"
      :max="propertyRange.max"
      :step="propertyStep"
      :disabled="propertyDisabled"
      @change="change"
      @input="update"
    />
  </div>
</template>

<script>
import _ from 'lodash';
import Vue from 'vue';
import { fnRun, evaluate } from '@/store/helpers';
import { formatPercent } from '@/filters';

export default {
  name: 'VSInput',
  props: {
    id: {
      type: String,
    },
    property: {
      type: Object,
      required: true,
    },
    type: {
      type: String,
      default: 'text',
    },
    filter: {
      type: String,
      default: 'same',
    },
    hideOverlay: {
      type: Boolean,
      default: false,
    },
    percent: {
      type: Boolean,
      value: false,
    },
  },
  data() {
    return {
      inputElement: {},
      showOverlay: true,
    };
  },
  computed: {
    propertyValue() {
      const value = this.fnRun(_.get(this.property, 'value'));
      return this.parseIfPercent(value);
    },
    propertyRange() {
      const range = _.get(this.property, 'range', {});

      return _.mapValues(range, (value) => {
        const returned = this.fnRun(value);

        if (_.isArray(returned)) {
          return _.map(returned, arrayValue => this.fnRun(arrayValue));
        }

        return evaluate(returned, 'number');
      });
    },
    propertyStep() {
      return this.fnRun(_.get(this.property, 'step', 1));
    },
    propertyDisabled() {
      return this.fnRun(_.get(this.property, 'disabled', false));
    },
  },
  methods: {
    parseIfPercent(value) {
      return this.percent ? formatPercent(value) : value;
    },
    generateEventListener(name) {
      return (event) => {
        this.$emit(name, event);
      };
    },
    hideOverlayElement() {
      this.showOverlay = false;
    },
    showOverlayElement() {
      if (this.hideOverlay) {
        this.showOverlay = false;
      } else {
        this.showOverlay = true;
      }
    },
    addEventListeners() {
      this.inputElement.addEventListener('focus', this.hideOverlayElement);
      this.inputElement.addEventListener('blur', this.showOverlayElement);
    },
    removeEventListeners() {
      this.inputElement.removeEventListener('focus', this.hideOverlayElement);
      this.inputElement.removeEventListener('blur', this.showOverlayElement);
    },
    // Overwritten on create
    filtered() {},
    change() {},
    update() {},
  },
  created() {
    this.filtered = Vue.filter(this.filter);
    this.change = this.generateEventListener('change');
    this.update = this.generateEventListener('update');
    const { state } = this.$store;
    this.fnRun = fnRun.bind(state, state);
  },
  mounted() {
    this.inputElement = this.$el.querySelector('.vs-input');

    if (this.hideOverlay) {
      this.showOverlay = false;
    }

    this.addEventListeners();
  },
  beforeDestroy() {
    this.removeEventListeners();
  },
};
</script>
